/**
 * Writing an instruction, section by section.
 *
 * A prompt for this product is a role, a stance, a rule about what may be
 * stated, a list of banned phrasings, an output format and a worked example.
 * Those are six different decisions, made at different times, and a single
 * 20,000-character textarea presents them as one - so the section that does the
 * most work sits in the same undifferentiated wall as the one nobody has
 * revisited since it was written, and adding one means guessing where it goes.
 *
 * Here each is its own box with its own heading, they can be reordered and
 * removed, and the sections the shipped instruction has that this draft does not
 * can be added back WITH THEIR REVIEWED WORDING - which is the difference
 * between "add the banned-expressions section" and a memory test.
 *
 * IT IS STILL JUST TEXT. `prompt-blocks.ts` parses on the way in and composes on
 * the way out; the stored prompt is the same plain string it has always been,
 * and 全文 below shows exactly it. That matters for trust as much as for
 * correctness: an author who cannot see what is actually sent will not believe
 * the boxes.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { TokenText, insertVariableInto } from './TokenText.tsx';
import { variableLabel } from '../variable-labels.ts';
import { Pill } from './ui.tsx';
import {
  BLOCK_HINTS,
  composeBlocks,
  emptyBlock,
  missingBlocks,
  missingOpening,
  parseBlocks,
  type PromptBlock,
} from '../prompt-blocks.ts';

export function PromptComposer({
  value,
  onChange,
  /** The wording this build ships, which is where addable sections come from. */
  shipped,
  /** Placeholders this prompt may name. Empty hides the chips entirely. */
  variables,
  label,
  hint,
}: {
  value: string;
  onChange: (next: string) => void;
  shipped: string;
  variables: string[];
  label: string;
  hint?: string;
}) {
  const [blocks, setBlocks] = useState<PromptBlock[]>(() => parseBlocks(value));
  const [raw, setRaw] = useState(false);
  /*
   * Which box a variable chip should insert into.
   *
   * The id is state and the element is a ref, and both are needed: the chips are
   * disabled until something has focus, which is a rendering decision, while the
   * caret position is read at click time and must not cause a render of its own.
   * Holding only the ref was a bug - focusing a box changed nothing on screen, so
   * the chips stayed greyed out however many times they were clicked.
   */
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const focused = useRef<HTMLElement | null>(null);
  /** Stable and ASCII: the label is Japanese and belongs in the text, not an id. */
  const fieldId = useId();

  /*
   * What this component last emitted.
   *
   * Every keystroke composes the blocks and hands the string up, and it comes
   * back as `value`. Without remembering what we sent, the effect below would
   * treat our own output as an outside change and re-parse mid-sentence - which
   * rebuilds every block with a new id and takes the caret with it.
   */
  const emitted = useRef(value);

  const push = (next: PromptBlock[]) => {
    setBlocks(next);
    const composed = composeBlocks(next);
    emitted.current = composed;
    onChange(composed);
  };

  // An outside change - a different version loaded, a reset - replaces the boxes.
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setBlocks(parseBlocks(value));
  }, [value]);

  const edit = (id: string, patch: Partial<PromptBlock>) =>
    push(blocks.map((block) => (block.id === id ? { ...block, ...patch } : block)));

  const move = (at: number, by: -1 | 1) => {
    const to = at + by;
    if (to < 0 || to >= blocks.length) return;
    const next = [...blocks];
    const [held] = next.splice(at, 1);
    next.splice(to, 0, held!);
    push(next);
  };

  /**
   * Insert a placeholder where the caret is, in whichever box has focus.
   *
   * The editor owns the caret, so this asks it rather than rewriting the text
   * around an offset: splicing a string and re-rendering would put the caret
   * back at the start and, mid-sentence in Japanese, discard an in-flight IME
   * composition.
   */
  /**
   * Put a placeholder into the text, wherever the author is working.
   *
   * Three cases, and all three used to do nothing:
   *
   *   A section, or the whole-prompt editor, has focus. The editor owns its caret, so it is asked rather
   *   than rewritten around an offset.
   *
   *   Nothing has focus, including a brand-new prompt with no sections at all.
   *   A chip pressed there used to be a no-op, which is indistinguishable from
   *   a broken button. A section is created if there is none, and the value
   *   goes at the end of the last one.
   */
  const insertVariable = (name: string) => {
    const token = `{{${name}}}`;

    if (focused.current) {
      insertVariableInto(focused.current, name);
      return;
    }

    // Whole-prompt mode with nothing focused: the end of the text.
    if (raw) {
      const next = `${value}${token}`;
      emitted.current = next;
      setBlocks(parseBlocks(next));
      onChange(next);
      return;
    }

    const last = blocks[blocks.length - 1];
    if (!last) {
      // Nothing to write into yet: give it somewhere to go rather than
      // swallowing the press.
      push([{ ...emptyBlock(), body: token }]);
      return;
    }
    edit(last.id, { body: last.body === '' ? token : `${last.body}${token}` });
  };

  const addable = missingBlocks(blocks, shipped);
  const opening = missingOpening(blocks, shipped);

  return (
    <div className="field">
      <div className="composer__top">
        {raw ? <label htmlFor={fieldId}>{label}</label> : <span className="field__label">{label}</span>}
        {/*
          The escape hatch, and the proof. Anybody who wants to paste a whole
          instruction, or to see exactly what the boxes add up to, switches here.
        */}
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setRaw(!raw)}
          aria-pressed={raw}
        >
          {raw ? 'ブロックで編集' : '全文を編集'}
        </button>
      </div>

      {/*
        In both modes.

        It used to be hidden whenever the author switched to 全文を編集, which is
        the mode somebody uses to paste a draft in and then fill the values -
        exactly when the placeholders are wanted - and the screen simply had no
        tags on it.
      */}
      <div className="composer__vars">
          {variables.length === 0 ? (
            /*
              Said, rather than shown as an empty space.

              A system prompt is static text - the placeholders belong to the
              request, which is the box below it - so this composer genuinely
              has none to offer. Rendering nothing left an author looking for
              the chips they had just seen elsewhere and concluding they had
              disappeared.
            */
            <span className="field__hint">
              この指示文は毎回そのまま送られます。記事ごとに変わる項目は、下の「依頼文」で挿入します。
            </span>
          ) : (
            <>
              {/*
                Above the sections, not below them.

                The chips were at the foot of the composer, after every section
                - so on a twelve-section prompt they were a screen and a half
                from the box being typed into, and an author clicking into a box
                watched them scroll out of sight. This is a toolbar; a toolbar
                belongs at the top of the thing it acts on.
              */}
              <div className="chips">
                {variables.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="chip"
                    // The English name, for anybody who needs to know what the
                    // chip will actually resolve to.
                    title={`{{${name}}}`}
                    onClick={() => insertVariable(name)}
                  >
                    {variableLabel(name)}
                  </button>
                ))}
              </div>
              <span className="field__hint">
                色付きの項目は、記事ごとに登録済みの情報へ置き換わります。未登録の項目は空欄になり、推測では埋めません。
                {focusedId === null && '挿入先のセクションを選んでから押すと、その位置に入ります。'}
              </span>
            </>
          )}
      </div>

      {raw ? (
        <>
          {/*
            The whole instruction in one editor - and the placeholders are chips
            here too.

            This was a plain textarea, so pressing a chip in this mode wrote
            `{{tone}}{{persona}}` in machine syntax on a screen where every other
            mode shows 「文体・トーン」. Same component, same round trip: what is
            stored is unchanged.
          */}
          <TokenText
            id={fieldId}
            minRows={18}
            value={value}
            variables={variables}
            placeholder="指示文の全文"
            onChange={(next) => {
              emitted.current = next;
              setBlocks(parseBlocks(next));
              onChange(next);
            }}
            onFocus={() => {
              focused.current = document.getElementById(fieldId);
              setFocusedId(fieldId);
            }}
          />
          {/*
            The literal string, read-only.

            This mode exists to prove what is actually sent, and chips are a
            rendering of that rather than the thing itself. An author who wants
            to see the text a model receives can open this and read it; nobody
            has to trust the chips to believe the prompt.
          */}
          <details className="composer__literal">
            <summary className="muted small">送信される文字列をそのまま見る</summary>
            <pre className="pre">{value}</pre>
          </details>
        </>
      ) : (
        <div className="composer">
          {blocks.map((block, at) => (
            <div className="composer__block" key={block.id}>
              <div className="composer__head">
                {at === 0 && block.heading === '' ? (
                  <span className="composer__heading composer__heading--fixed">
                    冒頭（役割・読者）
                  </span>
                ) : (
                  <input
                    className="composer__heading"
                    aria-label="セクション名"
                    value={block.heading}
                    placeholder="セクション名"
                    maxLength={120}
                    onChange={(event) => edit(block.id, { heading: event.target.value })}
                  />
                )}
                <div className="composer__tools">
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    title="上へ"
                    disabled={at === 0}
                    onClick={() => move(at, -1)}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    title="下へ"
                    disabled={at === blocks.length - 1}
                    onClick={() => move(at, 1)}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    title="このセクションを削除"
                    onClick={() => push(blocks.filter((entry) => entry.id !== block.id))}
                  >
                    削除
                  </button>
                </div>
              </div>

              {BLOCK_HINTS[block.heading.trim()] && (
                <p className="composer__hint">{BLOCK_HINTS[block.heading.trim()]}</p>
              )}

              {/*
                The placeholders are objects here, not `{{syntax}}` - see
                TokenText. What is stored is unchanged.
              */}
              <TokenText
                id={`block-${block.id}`}
                minRows={4}
                value={block.body}
                variables={variables}
                placeholder="この見出しの内容"
                onChange={(body) => edit(block.id, { body })}
                onFocus={() => {
                  focused.current = document.getElementById(`block-${block.id}`);
                  setFocusedId(block.id);
                }}
              />
            </div>
          ))}

          <div className="composer__add">
            {/*
              The opening paragraph - role and reader - which has no heading and
              so cannot appear in the list below. Offered rather than prefilled:
              a blank draft has never had this text put into it.
            */}
            {opening && (
              <button
                type="button"
                className="chip"
                title="役割と読者の説明を、既定の文面で追加します"
                onClick={() => push([opening, ...blocks])}
              >
                ＋ 冒頭（役割・読者）
              </button>
            )}
            {/*
              The shipped sections this draft is missing, by name, each carrying
              the wording it ships with. Adding one is a click; writing it from
              memory is how a guardrail ends up half-stated.
            */}
            {addable.map((block) => (
              <button
                key={block.heading}
                type="button"
                className="chip"
                title={BLOCK_HINTS[block.heading] ?? '既定の内容で追加します'}
                onClick={() => push([...blocks, { ...block, id: emptyBlock().id }])}
              >
                ＋ {block.heading}
              </button>
            ))}
            <button
              type="button"
              className="chip"
              onClick={() => push([...blocks, emptyBlock('新しいセクション')])}
            >
              ＋ 自由記述
            </button>
          </div>
        </div>
      )}

      {hint && <span className="field__hint">{hint}</span>}

      <p className="muted small" style={{ margin: '6px 0 0' }}>
        <Pill tone="muted">{composeBlocks(blocks).length.toLocaleString('ja-JP')}文字</Pill>{' '}
        送信されるのは、上のセクションを順につないだ1つの文章です。
      </p>
    </div>
  );
}
